import React from 'react';
import { motion, type Variants } from 'framer-motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';

interface AnimateOnScrollProps {
  children: React.ReactNode;
  variant?: 'fadeUp' | 'fadeIn' | 'scaleIn' | 'slideStart' | 'slideEnd';
  delay?: number;
  duration?: number;
  className?: string;
  once?: boolean;
  /** Skip the in-view check and animate immediately on mount.
   *  Use for above-the-fold content (hero, first section) so it never
   *  flashes hidden when the viewport already shows it on initial paint. */
  eager?: boolean;
}

const variants: Record<string, Variants> = {
  fadeUp: {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0 },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  scaleIn: {
    hidden: { opacity: 0, scale: 0.92 },
    visible: { opacity: 1, scale: 1 },
  },
  slideStart: {
    hidden: { opacity: 0, x: -24 },
    visible: { opacity: 1, x: 0 },
  },
  slideEnd: {
    hidden: { opacity: 0, x: 24 },
    visible: { opacity: 1, x: 0 },
  },
};

const AnimateOnScroll: React.FC<AnimateOnScrollProps> = ({
  children,
  variant = 'fadeUp',
  delay = 0,
  duration = 0.5,
  className = '',
  once = true,
  eager = false,
}) => {
  const prefersReducedMotion = useReducedMotion();

  // When user prefers reduced motion: render content immediately at the
  // visible state with zero-duration transitions; skip whileInView entirely
  // so content does not flash hidden.
  if (prefersReducedMotion) {
    return (
      <motion.div
        initial={false}
        animate="visible"
        variants={variants[variant]}
        transition={{ duration: 0 }}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  // Eager: skip viewport gating, animate from initial→animate on mount.
  // Otherwise: trigger when 10% of the element is visible. Avoids the
  // bug where above-fold content stays at opacity:0 because the negative
  // margin pushed the trigger below the initial viewport.
  if (eager) {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={variants[variant]}
        transition={{ duration, delay, ease: [0.25, 0.1, 0.25, 1] }}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: 0.1 }}
      variants={variants[variant]}
      transition={{ duration, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default AnimateOnScroll;

// Stagger wrapper for lists
export const StaggerContainer: React.FC<{
  children: React.ReactNode;
  className?: string;
  staggerDelay?: number;
}> = ({ children, className = '', staggerDelay = 0.08 }) => {
  const prefersReducedMotion = useReducedMotion();
  if (prefersReducedMotion) {
    return (
      <motion.div
        initial={false}
        animate="visible"
        transition={{ staggerChildren: 0 }}
        className={className}
      >
        {children}
      </motion.div>
    );
  }
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      transition={{ staggerChildren: staggerDelay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  const prefersReducedMotion = useReducedMotion();
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 16 },
        visible: {
          opacity: 1,
          y: 0,
          transition: prefersReducedMotion
            ? { duration: 0 }
            : { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};
